/**
 * The four things a party committee does with money, and the words for them.
 *
 * Kept in one module because the categories appear on four screens and the
 * whole feature turns on their never being added together. $411m of the
 * money here is 24A — advertising bought to attack a candidate, filed by the
 * FEC under the name of the candidate it attacks. Beside 24C coordinated
 * spending on the same page, a reader who adds two rows gets a figure that
 * is wrong about the amount, the direction and the recipient at once: the
 * DSCC spent $12.3m attacking Mike Rogers and $2.0m with Elissa Slotkin's
 * campaign in the same Senate race, and $14m of DSCC support for Slotkin did
 * not happen.
 *
 * So every amount on this side of the site travels with its category, the
 * category carries a direction, and nothing here returns a total.
 */

import type { Effect, MemberCommitteeMoney, PartyCommittee } from "../api";

/** What a category is, in the words the page uses.
 *
 *  `direction` is the load-bearing field. Everything else is presentation;
 *  this one decides whether a candidate's name means the money helped them
 *  or was aimed at them. */
export type EffectFacts = {
  /** The heading, written from the committee's side: what it did. */
  label: string;
  /** The same thing in two or three words, for a pill beside an amount. */
  tag: string;
  /** The word that joins an amount to a candidate's name: "$10.5m against
   *  David McCormick". Carried per category because it is the shortest
   *  correct way to say which way the money was pointing, and a row that
   *  names a person without it is the misreading this section guards. */
  preposition: string;
  direction: "for" | "against";
  /** The FEC transaction codes this reads. Shown so that the reading can be
   *  checked against the filing rather than taken on trust. */
  codes: string[];
  blurb: string;
};

/** What the candidate was given, what was spent with them, what was spent
 *  for them, what was spent against them. Mirrors `_EFFECT_ORDER` in the
 *  store, so the categories do not shuffle between the API's ordering and
 *  the page's and invite a reader to compare the wrong pair. */
export const EFFECT_ORDER: Effect[] = ["direct", "coordinated", "supporting", "opposing"];

export const EFFECTS: Record<Effect, EffectFacts> = {
  direct: {
    label: "Given to the campaign",
    tag: "to them",
    preposition: "to",
    direction: "for",
    codes: ["24K", "24Z", "24P"],
    blurb:
      "A cheque the campaign banked, or goods and services given in kind. It is the only one of the four the candidate controls, it counts against the ordinary contribution limit, and across these committees it is the smallest of the four by a factor of forty.",
  },
  coordinated: {
    label: "Coordinated party spending",
    tag: "with them",
    preposition: "with",
    direction: "for",
    codes: ["24C"],
    blurb:
      "Spent by the party in agreement with the campaign, on the campaign's behalf — the campaign never banks it, so it is not a contribution, and it is not independent either. It has a statutory limit of its own under 52 USC 30116(d), which is why it runs to tens of millions where direct giving runs to thousands.",
  },
  supporting: {
    label: "Spent supporting them",
    tag: "for them",
    preposition: "for",
    direction: "for",
    codes: ["24E", "24F"],
    blurb:
      "Advertising bought to argue for the candidate's election, and letters a committee sends its own members, both without the campaign's involvement. There is no ceiling on it.",
  },
  opposing: {
    label: "Spent attacking them",
    tag: "against them",
    preposition: "against",
    direction: "against",
    codes: ["24A", "24N"],
    blurb:
      "Advertising bought to argue against a candidate's election. The FEC files it under the candidate it attacks, so the person named on one of these rows is the person the money was aimed at — it is not money they received, and it is not support for them.",
  },
};

/** The key the API uses for a transaction code the corpus refuses to
 *  classify. A JSON object cannot have a null key, so Python's `None`
 *  arrives as this. */
export const UNCLASSIFIED = "null";

export const UNCLASSIFIED_FACTS: EffectFacts = {
  label: "Not classified",
  tag: "unclassified",
  preposition: "recorded against",
  direction: "for",
  codes: ["24H", "24R"],
  blurb:
    "An honorarium paid to the candidate personally, or recount spending, which argues for no election because the election has happened. Neither belongs in any of the four, so neither is put in one.",
};

export type EffectRow = { key: string; amount: number; facts: EffectFacts };

/**
 * One row per category, in order, for a `money_out` mapping.
 *
 * All four always, including the ones a committee spent nothing in. The
 * finding on the index page is that opposing dwarfs everything and direct
 * giving is a rounding error, and a row that is not drawn cannot show a
 * reader that the rounding error is a rounding error.
 *
 * Anything the corpus could not classify goes last rather than into the
 * nearest bucket, and is never dropped: it is real money.
 */
export function effectRows(out: Record<string, number>): EffectRow[] {
  const known: EffectRow[] = EFFECT_ORDER.map((key) => ({
    key,
    amount: out[key] ?? 0,
    facts: EFFECTS[key],
  }));
  const rest = Object.keys(out)
    .filter((key) => !(EFFECT_ORDER as string[]).includes(key))
    .map((key) => ({ key, amount: out[key], facts: UNCLASSIFIED_FACTS }));
  return [...known, ...rest];
}

/** The largest single category, or nothing for a committee that spent
 *  nothing. Used to say what the biggest thing a committee did was, rather
 *  than asserting it in prose that could go stale. */
export function biggest(out: Record<string, number>): EffectRow | null {
  const rows = effectRows(out).filter((row) => row.amount > 0);
  if (rows.length === 0) return null;
  return rows.reduce((top, row) => (row.amount > top.amount ? row : top));
}

/**
 * The denominator every bar on the index is drawn against.
 *
 * One scale across all four committees, not one per committee. A per
 * committee scale draws the DCCC's $157m of attacking money and the NRSC's
 * $55m at the same width, and comparing those is most of what the page is
 * for. Never zero, so a corpus with no spending in it divides safely.
 */
export function ceiling(committees: PartyCommittee[]): number {
  const every = committees.flatMap((c) => Object.values(c.money_out));
  return Math.max(1, ...every);
}

/** What a committee was given that can never reach a candidate: the
 *  headquarters, recount and convention accounts, which have their own far
 *  higher limits. Clamped at zero rather than trusted to be positive. */
export function nonFederal(committee: PartyCommittee): number {
  return Math.max(0, committee.money_in - committee.money_in_federal);
}

/** The four national campaign committees, by the name the FEC files them
 *  under.
 *
 *  Mirrors `NATIONAL_PARTY_COMMITTEES` in the store, and matched on name for
 *  the reason the store gives: a committee id typed into a codebase is wrong
 *  silently and forever, and what it would be wrong about is whose money the
 *  reader is looking at. The index endpoint answers with every party
 *  committee that moved money — a couple of hundred of them, mostly county
 *  parties — and these are the four the LD-203 record can also name. */
const NATIONAL = ["DSCC", "NRSC", "DCCC", "NRCC"];

/** What the four acronyms stand for, and who they raise for.
 *
 *  The FEC's committee master files all four under the acronym alone, so
 *  without this the page asks a reader to know that DSCC and DCCC differ by
 *  chamber and not by party. Both halves are the committee's own registered
 *  name and its own chamber, not a characterisation of it. */
export const NAMED: Record<string, { full: string; whose: string }> = {
  DSCC: { full: "Democratic Senatorial Campaign Committee", whose: "Senate Democrats" },
  NRSC: { full: "National Republican Senatorial Committee", whose: "Senate Republicans" },
  DCCC: { full: "Democratic Congressional Campaign Committee", whose: "House Democrats" },
  NRCC: { full: "National Republican Congressional Committee", whose: "House Republicans" },
};

/**
 * $75.7m, $991k, $500.
 *
 * One decimal place in the millions, where `brief` in `party.ts` drops it
 * above ten million. That rounding is right on a card ranking firms and
 * wrong here: the argument this section makes is the distance between $9.1m
 * given directly and $411.4m spent attacking, and "$9m against $411m" reads
 * as a rounding of the same kind of thing.
 */
export function figure(value: number): string {
  if (!value) return "$0";
  const sign = value < 0 ? "−" : "";
  const size = Math.abs(value);
  if (size >= 1_000_000) return `${sign}$${(size / 1_000_000).toFixed(1)}m`;
  if (size >= 1_000) return `${sign}$${Math.round(size / 1_000)}k`;
  return `${sign}$${Math.round(size)}`;
}

/** A share as a whole percent, or a dash when there is nothing to divide.
 *  A dash rather than 0%, which is a claim about the numerator. */
export function share(part: number, whole: number): string {
  return whole ? `${Math.round((part / whole) * 100)}%` : "—";
}

export function national(committees: PartyCommittee[]): PartyCommittee[] {
  const held = new Map(committees.map((c) => [c.name.trim().toUpperCase(), c]));
  return NATIONAL.map((name) => held.get(name)).filter((c) => c !== undefined);
}

/** Everything else the endpoint answered with: the state and county parties,
 *  and the DNC and RNC, which are national but are not campaign committees
 *  and take no lobbyist money here. Counted rather than dropped. */
export function alsoHeld(committees: PartyCommittee[]): PartyCommittee[] {
  const four = new Set(NATIONAL);
  return committees
    .filter((c) => !four.has(c.name.trim().toUpperCase()))
    .sort((a, b) => ceiling([b]) - ceiling([a]));
}

/**
 * Of the committees beyond the four, how many the record could name and how
 * much they were given.
 *
 * Counted here rather than written into the sentence that quotes it. That
 * sentence tells a reader a dash means money nobody could resolve and not a
 * committee that was given nothing, which is a claim about how good the
 * matching is — and a figure typed beside it goes stale the first time the
 * matching improves, leaving the page confidently wrong about its own
 * honesty.
 */
export function resolvedMoneyIn(committees: PartyCommittee[]): {
  named: number;
  dashed: number;
  amount: number;
} {
  const others = alsoHeld(committees);
  const named = others.filter((c) => (c.money_in ?? 0) > 0);
  return {
    named: named.length,
    dashed: others.length - named.length,
    amount: named.reduce((sum, c) => sum + (c.money_in ?? 0), 0),
  };
}

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/** "January 2021 to June 2026", for a `covers` window.
 *
 *  Split out of the ISO string rather than put through `Date`. Parsing
 *  "2021-01-01" gives UTC midnight, which in every timezone west of
 *  Greenwich is the previous December — so a naive rendering reports the
 *  wrong month and, on a January date, the wrong year. The two halves of a
 *  committee page exist to be compared, and being a month out would make
 *  that comparison quietly wrong. */
export function period(covers: { first: string | null; last: string | null } | null): string {
  if (!covers?.first || !covers.last) return "no dated filings";
  return `${asMonth(covers.first)} to ${asMonth(covers.last)}`;
}

function asMonth(iso: string): string {
  const [year, month] = iso.split("-");
  return `${MONTHS[Number(month) - 1] ?? month} ${year}`;
}

export type MemberSides = {
  /** Money spent attacking them, which they never received. */
  against: MemberCommitteeMoney[];
  /** Money spent for them, in whichever of the three ways. Kept as separate
   *  rows and never added: a cheque the campaign banked and an advertisement
   *  a party bought are not the same thing happening. */
  behind: MemberCommitteeMoney[];
  unclassified: MemberCommitteeMoney[];
};

/**
 * Everything spent attacking one member, across every committee that did.
 *
 * The one sum on this side of the site, and it is allowed because it adds
 * like with like: two committees buying advertising against the same person
 * are doing the same thing, so the total is a real quantity with a name.
 *
 * There is deliberately no counterpart for the other side. Money spent for a
 * candidate is three different acts — a cheque they banked, a buy made with
 * their campaign, a buy made without it — and adding those is exactly the
 * error this feature exists to avoid.
 */
export function spentAttacking(rows: MemberCommitteeMoney[]): number {
  return sideOf(rows).against.reduce((sum, row) => sum + row.amount, 0);
}

/**
 * A member's party committee rows, split by which side of them the money was
 * on, largest first within each side.
 *
 * The split is the point. Elissa Slotkin's largest entry is $11.0m from the
 * NRSC and it was spent attacking her, so her page must not be able to put
 * that figure beside her name without saying which way it pointed.
 */
export function sideOf(rows: MemberCommitteeMoney[]): MemberSides {
  const bySize = [...rows].sort((a, b) => b.amount - a.amount);
  return {
    against: bySize.filter((row) => row.effect && EFFECTS[row.effect].direction === "against"),
    behind: bySize.filter((row) => row.effect && EFFECTS[row.effect].direction === "for"),
    unclassified: bySize.filter((row) => !row.effect),
  };
}
